import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { type Observable, map } from 'rxjs';

import type { ApiEnvelope } from '../http/api-envelope';
import type { AiAnswerResponse, AiStatusResponse, AiTurn } from './ai.models';

/** clrAIREST: "Ask Celeri AI". */
@Injectable()
export abstract class AiApi {
  abstract status(): Observable<AiStatusResponse>;
  abstract ask(message: string, history: readonly AiTurn[]): Observable<AiAnswerResponse>;
  abstract confirm(proposalId: string): Observable<AiAnswerResponse>;
  abstract cancel(proposalId: string): Observable<void>;
  /** The words spoken in a recording (WAV); language is a hint. */
  abstract transcribe(audio: Blob, language: string): Observable<string>;
}

@Injectable()
export class HttpAiApi extends AiApi {
  private readonly http = inject(HttpClient);

  override status(): Observable<AiStatusResponse> {
    return this.http
      .get<ApiEnvelope<AiStatusResponse | null>>('/ai/status')
      .pipe(map((response) => response.data ?? { ready: false }));
  }

  override ask(message: string, history: readonly AiTurn[]): Observable<AiAnswerResponse> {
    return this.http
      .post<ApiEnvelope<AiAnswerResponse | null>>('/ai/ask', { message, history })
      .pipe(map((response) => response.data ?? {}));
  }

  override confirm(proposalId: string): Observable<AiAnswerResponse> {
    return this.http
      .post<ApiEnvelope<AiAnswerResponse | null>>(
        `/ai/proposals/${encodeURIComponent(proposalId)}/confirm`,
        {},
      )
      .pipe(map((response) => response.data ?? {}));
  }

  override cancel(proposalId: string): Observable<void> {
    return this.http
      .post<void>(`/ai/proposals/${encodeURIComponent(proposalId)}/cancel`, {})
      .pipe(map(() => undefined));
  }

  override transcribe(audio: Blob, language: string): Observable<string> {
    const form = new FormData();

    form.append('audio', audio, 'recording.wav');
    form.append('language', language);

    return this.http
      .post<ApiEnvelope<{ text?: string } | null>>('/ai/transcribe', form)
      .pipe(map((response) => response.data?.text?.trim() ?? ''));
  }
}
